import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react'
import { forwardRef, useEffect, useState } from 'react'

/* --------------------------------------------------------------------- *
 *  CAPS LABEL — all-caps mono label
 * --------------------------------------------------------------------- */
export function SmallCapsLabel({
  children,
  className = '',
  as: Component = 'div',
  tone = 'muted',
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'span' | 'h1' | 'h2' | 'h3' | 'label' | 'p'
  tone?: 'fg' | 'muted' | 'accent' | 'dim' | 'danger' | 'warn'
}) {
  const toneClass = {
    fg: 'text-fg',
    muted: 'text-fg-mute',
    accent: 'text-accent',
    dim: 'text-fg-dim',
    danger: 'text-danger',
    warn: 'text-warn',
  }[tone]
  return (
    <Component
      className={`font-mono uppercase tracking-[0.08em] text-[12.5px] ${toneClass} ${className}`}
    >
      {children}
    </Component>
  )
}

/* --------------------------------------------------------------------- *
 *  STATUS DOT — filled square block
 * --------------------------------------------------------------------- */
export function StatusDot({
  tone = 'fg',
  pulse = false,
  size = 9,
  className = '',
}: {
  tone?: 'fg' | 'accent' | 'warn' | 'danger' | 'dim' | 'info'
  pulse?: boolean
  size?: number
  className?: string
}) {
  const bg = {
    fg: 'bg-fg',
    accent: 'bg-accent',
    warn: 'bg-warn',
    danger: 'bg-danger',
    dim: 'bg-fg-dim',
    info: 'bg-info',
  }[tone]
  return (
    <span
      aria-hidden
      className={`inline-block ${bg} ${pulse ? 'animate-pulse-dot' : ''} ${className}`}
      style={{ width: size, height: size }}
    />
  )
}

/* --------------------------------------------------------------------- *
 *  BRACKET BUTTON — [ label ]
 *  Primary variant uses the phosphor accent; invert on hover.
 * --------------------------------------------------------------------- */
interface LetterpressProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'danger' | 'secondary'
  shortcut?: string
  size?: 'sm' | 'md' | 'lg'
  icon?: ReactNode
}

export const LetterpressButton = forwardRef<HTMLButtonElement, LetterpressProps>(
  function BracketButton(
    { variant = 'secondary', shortcut, size: _size = 'md', icon, className = '', children, ...rest },
    ref,
  ) {
    const variantClass = {
      primary: 'btn-primary',
      secondary: 'btn',
      ghost: 'btn-ghost',
      danger: 'btn-danger',
    }[variant]
    const baseClass = variant === 'primary' || variant === 'danger' ? 'btn' : ''
    return (
      <button
        ref={ref}
        className={`${baseClass} ${variantClass} ${className}`}
        {...rest}
      >
        <span className="text-fg-dim">[</span>
        {icon && <span className="shrink-0">{icon}</span>}
        <span>{children}</span>
        {shortcut && (
          <span className="text-[12px] text-fg-dim ml-1">{shortcut}</span>
        )}
        <span className="text-fg-dim">]</span>
      </button>
    )
  },
)

/* --------------------------------------------------------------------- *
 *  INPUT / TEXTAREA — hairline rectangle with $ prompt option
 * --------------------------------------------------------------------- */
interface HairlineInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  helper?: string
  mono?: boolean
  prompt?: '$' | '>' | null
}

export const HairlineInput = forwardRef<HTMLInputElement, HairlineInputProps>(
  function ConsoleInput(
    { label, helper, prompt = null, className = '', id, ...rest },
    ref,
  ) {
    const inputId = id || (label ? `in-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined)
    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="block font-mono uppercase tracking-[0.08em] text-[11.5px] text-fg-mute"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {prompt && (
            <span
              aria-hidden
              className="absolute left-2.5 top-1/2 -translate-y-1/2 font-mono text-[15.5px] text-accent pointer-events-none"
            >
              {prompt}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`input ${prompt ? 'pl-6' : ''} ${className}`}
            {...rest}
          />
        </div>
        {helper && (
          <div className="font-mono text-[12px] text-fg-dim">{helper}</div>
        )}
      </div>
    )
  },
)

interface HairlineTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  helper?: string
}

export const HairlineTextarea = forwardRef<HTMLTextAreaElement, HairlineTextareaProps>(
  function ConsoleTextarea({ label, helper, className = '', id, ...rest }, ref) {
    const taId = id || (label ? `ta-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined)
    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={taId}
            className="block font-mono uppercase tracking-[0.08em] text-[12.5px] text-fg-mute"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={taId}
          className={`input resize-y ${className}`}
          {...rest}
        />
        {helper && (
          <div className="font-mono text-[13px] text-fg-dim">{helper}</div>
        )}
      </div>
    )
  },
)

/* --------------------------------------------------------------------- *
 *  HOST CHIP — mono tag; live dot if active
 * --------------------------------------------------------------------- */
export function BureauChip({
  host,
  onClick,
  role,
  size: _size = 'md',
  active = false,
}: {
  host: string
  onClick?: () => void
  role?: string
  size?: 'sm' | 'md'
  active?: boolean
}) {
  const Component: 'button' | 'span' = onClick ? 'button' : 'span'
  return (
    <Component
      onClick={onClick}
      className={`inline-flex items-center gap-2 font-mono text-[14.5px] px-2 py-0.5 border ${
        active ? 'border-accent text-accent' : 'border-line text-fg'
      } ${onClick ? 'cursor-pointer hover:bg-surface' : ''} transition-colors`}
    >
      <StatusDot tone={active ? 'accent' : 'dim'} pulse={active} size={7} />
      <span className="truncate max-w-[220px]">{host}</span>
      {role && <span className="text-fg-dim">· {role}</span>}
    </Component>
  )
}

/* --------------------------------------------------------------------- *
 *  CURSOR — blinking caret for streaming text
 * --------------------------------------------------------------------- */
export function WireCursor({ className = '' }: { className?: string }) {
  return <span aria-hidden className={`caret ml-0.5 ${className}`} />
}

/* --------------------------------------------------------------------- *
 *  Kept for compatibility; no-op.
 * --------------------------------------------------------------------- */
export function DropCap({ children, className = '' }: { children: string; className?: string }) {
  return <span className={className}>{children}</span>
}

/* --------------------------------------------------------------------- *
 *  STAMP FLOURISH — tiny "OK · hh:mm" toast near the composer
 * --------------------------------------------------------------------- */
export function StampFlourish({ label }: { label: string }) {
  return (
    <span
      className="pointer-events-none inline-flex items-center gap-[0.4ch] font-mono text-[12px] text-fg-dim animate-fade-in"
      aria-hidden
    >
      <span className="text-fg-dim">[</span>
      <span className="text-accent">{label}</span>
      <span className="text-fg-dim">]</span>
    </span>
  )
}

/* --------------------------------------------------------------------- *
 *  RULE BAND — single hairline divider
 * --------------------------------------------------------------------- */
export function RuleBand({ className = '' }: { className?: string; variant?: unknown; ornament?: unknown }) {
  return <div aria-hidden className={`h-px w-full bg-line ${className}`} />
}

/* --------------------------------------------------------------------- *
 *  EMPTY STATE — ASCII-frame headline
 * --------------------------------------------------------------------- */
export function EmptyPlate({
  headline,
  sub,
  action,
}: {
  headline: ReactNode
  sub?: ReactNode
  action?: ReactNode
  ornament?: boolean
}) {
  return (
    <div className="flex flex-col items-center text-center py-10 px-6 max-w-lg mx-auto animate-fade-in">
      <div className="font-mono text-fg-dim text-[12px] mb-4 select-none">
        ┌──────────────────────────┐
        <br />│ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; │
        <br />└──────────────────────────┘
      </div>
      <div className="font-mono font-semibold text-[17.5px] text-fg">{headline}</div>
      {sub && (
        <div className="mt-3 font-mono text-[14px] text-fg-mute leading-relaxed max-w-md">
          {sub}
        </div>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  )
}

/* --------------------------------------------------------------------- *
 *  SIDE PANEL — right-edge slide-in; hairline frame
 * --------------------------------------------------------------------- */
export function SidePanel({
  open,
  onClose,
  title,
  subtitle,
  children,
  width = 'w-[500px]',
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  children: ReactNode
  width?: string
}) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      <div
        className="absolute inset-0 bg-bg/80 animate-fade-in"
        onClick={onClose}
      />
      <aside
        className={`absolute top-0 right-0 bottom-0 ${width} max-w-[94vw] bg-bg-raise border-l border-line overflow-y-auto animate-slide-in-right`}
      >
        <div className="px-5 py-3 bg-surface border-b border-line sticky top-0 z-10 flex items-center justify-between gap-4">
          <div className="min-w-0 flex items-center gap-2 font-mono">
            <span className="text-accent text-[15.5px]">┌─</span>
            <span className="text-fg text-[15px] font-semibold truncate">{title}</span>
            {subtitle && (
              <span className="text-fg-dim text-[12.5px] truncate">· {subtitle}</span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close panel"
            className="btn btn-ghost"
          >
            <span className="text-fg-dim">[</span>
            <span>✕</span>
            <span className="text-fg-dim">]</span>
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
      </aside>
    </div>
  )
}

/* --------------------------------------------------------------------- *
 *  TOOL CALL CHIP — collapsible framed block
 * --------------------------------------------------------------------- */
export function FieldReportChip({
  header,
  meta,
  children,
  defaultOpen = false,
  tone = 'accent',
}: {
  header: ReactNode
  meta?: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  tone?: 'accent' | 'danger'
}) {
  const [open, setOpen] = useState(defaultOpen)
  const color = tone === 'danger' ? 'text-danger' : 'text-accent'
  return (
    <div className="my-2 animate-fade-in font-mono text-[13px]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left flex items-center gap-2 hover:text-fg text-fg-soft transition-colors"
      >
        <span className={color}>
          {open ? '▼' : '▶'}
        </span>
        <span className={`${color}`}>tool</span>
        <span className="text-fg-dim">·</span>
        <span className="text-fg truncate flex-1">{header}</span>
        {meta && <span className="text-fg-dim shrink-0">{meta}</span>}
      </button>
      {open && (
        <div className="mt-1 border-l border-line pl-3 text-[13px] text-fg whitespace-pre-wrap break-all">
          {children}
        </div>
      )}
    </div>
  )
}
